/**
 * Group Management for Quote Line Items
 */
import { isMobile, getLangPrefix } from './modules/utils.js';
import Sortable from 'sortablejs';

// Global variable to store quote ID
window.quoteId = null;

// Exported init function called by main.js
export function init() {
    initializeGroupManagement();
}

/**
 * Initialize all group management features
 */
// Document-level listeners are delegated and must be bound exactly once;
// re-binding them after every HTMX swap made a header click toggle the
// group N times (an even N looked like nothing happened).
let documentListenersBound = false;

function initializeGroupManagement() {
    // Get quote ID from page (should be set in the template)
    const quoteIdElement = document.querySelector('[data-quote-id]');
    if (quoteIdElement) {
        window.quoteId = quoteIdElement.dataset.quoteId;
    }
    
    // Setup drag and drop
    setupGroupDragDrop();
    
    if (!documentListenersBound) {
        documentListenersBound = true;
        setupCollapsibleGroups();
        setupGroupKeyboardShortcuts();
        if (isMobile()) {
            setupMobileGroupGestures();
        }
    }
}

/**
 * Setup collapsible groups functionality
 */
function setupCollapsibleGroups() {
    document.addEventListener('click', function(e) {
        // Clicks on the header's own controls (rename, delete, drag) must not
        // also collapse the group.
        const groupHeader = e.target.closest('.group-header');
        if (!groupHeader || e.target.closest(
            'button, a, input, [hx-get], [hx-post], [hx-put], [hx-delete], .group-drag-handle'
        )) {
            return;
        }

        // Swapped lists wrap each group in .group-container; the first
        // render has the header followed by its items instead.
        const groupContainer = groupHeader.closest('.group-container');
        if (groupContainer) {
            toggleGroupCollapse(groupContainer);
        } else if (groupHeader.nextElementSibling) {
            groupHeader.nextElementSibling.classList.toggle('hidden');
            groupHeader.classList.toggle('collapsed');
        }
    });
}

/**
 * Toggle group collapse state
 */
function toggleGroupCollapse(groupContainer) {
    groupContainer.classList.toggle('group-collapsed');
    
    // Add chevron icon animation if present
    const chevron = groupContainer.querySelector('.group-chevron');
    if (chevron) {
        chevron.classList.toggle('rotate-180');
    }
    
    // Smooth animation for collapse/expand
    const groupItems = groupContainer.querySelector('.group-items');
    if (groupItems) {
        if (groupContainer.classList.contains('group-collapsed')) {
            groupItems.style.maxHeight = '0';
            groupItems.style.overflow = 'hidden';
            groupItems.style.transition = 'max-height 0.3s ease-out';
        } else {
            groupItems.style.maxHeight = groupItems.scrollHeight + 'px';
            setTimeout(() => {
                groupItems.style.maxHeight = '';
                groupItems.style.overflow = '';
            }, 300);
        }
    }
}

/**
 * Setup drag and drop for groups and line items
 * Integrates with existing line_item_management.js system
 */
function setupGroupDragDrop() {
    // Let the existing line item management system handle line item drag and drop
    // We only handle group-specific functionality here
    
    // Make groups sortable (this is new functionality - for reordering entire groups)
    const groupsList = document.querySelector('.groups-list');
    if (groupsList) {
        new Sortable(groupsList, {
            animation: 150,
            handle: '.group-drag-handle',
            ghostClass: 'sortable-ghost',
            dragClass: 'sortable-drag',
            onEnd: function(evt) {
                updateGroupOrder(evt);
            }
        });
    }
    
    // No need for setupGroupTransferDetection anymore - 
    // line item management handles this via the onEnd callback
}


/**
 * Update group order after drag and drop
 */
function updateGroupOrder(evt) {
    // Group visual order is managed client-side via SortableJS
}

/**
 * Update line item's group via HTMX
 */
function updateLineItemGroup(itemId, newGroup, orderedIds = []) {
    if (!window.quoteId) {
        return;
    }
    
    const prefix = getLangPrefix();
    const url = `${prefix}/quotes/${window.quoteId}/line-items/${itemId}/update-group/`;
    
    // Get CSRF token
    const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]');
    if (!csrfToken) {
        return;
    }
    
    // Use HTMX ajax function instead of creating form
    htmx.ajax('POST', url, {
        values: {
            'group': newGroup,
            'item_ids': orderedIds.join(','),
            'csrfmiddlewaretoken': csrfToken.value
        },
        target: '#line-items-list',
        swap: 'innerHTML'
    });
}


/**
 * Setup keyboard shortcuts for group management
 */
function setupGroupKeyboardShortcuts() {
    document.addEventListener('keydown', function(e) {
        // Ctrl+G to create new group
        if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
            e.preventDefault();
            const addGroupBtn = document.querySelector('.add-group-btn');
            if (addGroupBtn) {
                addGroupBtn.click();
            }
        }
        
        // Delete key to delete focused group (if group header has focus)
        if (e.key === 'Delete' && e.target.closest('.group-header')) {
            const groupHeader = e.target.closest('.group-header');
            const deleteBtn = groupHeader.querySelector('.group-delete-btn');
            if (deleteBtn && confirm(deleteBtn.getAttribute('hx-confirm'))) {
                deleteBtn.click();
            }
        }
    });
}

/**
 * Setup mobile-specific gestures
 */
function setupMobileGroupGestures() {
    let touchStartX = 0;
    let touchStartY = 0;
    
    document.addEventListener('touchstart', function(e) {
        const groupHeader = e.target.closest('.group-header');
        if (groupHeader) {
            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
        }
    }, { passive: true });
    
    document.addEventListener('touchend', function(e) {
        const groupHeader = e.target.closest('.group-header');
        if (groupHeader) {
            const touchEndX = e.changedTouches[0].clientX;
            const touchEndY = e.changedTouches[0].clientY;
            
            const swipeDistanceX = touchStartX - touchEndX;
            const swipeDistanceY = Math.abs(touchStartY - touchEndY);
            
            // Check for horizontal swipe (not vertical)
            if (swipeDistanceX > 100 && swipeDistanceY < 50) {
                // Swipe left to show delete confirmation
                showGroupDeleteConfirm(groupHeader);
            }
        }
    }, { passive: true });
}

/**
 * Show delete confirmation for mobile swipe
 */
function showGroupDeleteConfirm(groupHeader) {
    const deleteBtn = groupHeader.querySelector('.group-delete-btn');
    if (deleteBtn) {
        // Add visual feedback
        groupHeader.classList.add('bg-red-50');
        setTimeout(() => {
            groupHeader.classList.remove('bg-red-50');
        }, 2000);
        
        // Trigger delete button click
        if (confirm(deleteBtn.getAttribute('hx-confirm'))) {
            deleteBtn.click();
        }
    }
}

// Global flag to prevent multiple reinitializations
let reinitializationInProgress = false;

// Re-initialize after HTMX swaps
document.body.addEventListener('htmx:afterSwap', function(event) {
    // Re-initialize drag and drop for new elements
    if (event.target.id === 'line-items-list' || event.target.id === 'groups-container') {
        if (reinitializationInProgress) {
            return;
        }
        
        reinitializationInProgress = true;
        
        // Re-initialize group management features (but not drag-and-drop - that's handled by line item management)
        setTimeout(() => {
            // Get quote ID from page
            const quoteIdElement = document.querySelector('[data-quote-id]');
            if (quoteIdElement) {
                window.quoteId = quoteIdElement.dataset.quoteId;
            }
            
            // Delegated listeners are already bound once; nothing to re-add.
            
            // Reset flag after a delay
            setTimeout(() => {
                reinitializationInProgress = false;
            }, 200);
        }, 50); // Earlier than line item management to ensure functions are available
    }
});

// Re-initialize after HTMX settles
document.body.addEventListener('htmx:afterSettle', function(event) {
    // Focus on edited group name input
    const groupInput = event.target.querySelector('.group-input');
    if (groupInput) {
        groupInput.focus();
        // Select all text for easy replacement
        groupInput.select();
    }
});

// Expose updateLineItemGroup function to global scope for line item management integration
window.updateLineItemGroup = updateLineItemGroup;